import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Typography, Button } from '@mui/material';
import ThemeBackground from '../Layout/ThemeBackground';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';

interface Client {
  _id: string;
  firstName: string;
  lastName: string;
  email?: string;
  hasHadSessions?: boolean;
  numberOfSessions?: number;
  paymentDetails?: { outstandingBalance?: number };
}

type FinalAssessmentOutcome = 'completed' | 'declined' | 'not_possible' | 'clinically_inappropriate' | 'other';

const OffboardingClient: React.FC = () => {
  const { id: clientId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === 'administrator';
  const [client, setClient] = useState<Client | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [offboardingReason, setOffboardingReason] = useState('');
  const [otherReason, setOtherReason] = useState('');
  const [finalAssessmentOutcome, setFinalAssessmentOutcome] = useState<FinalAssessmentOutcome | ''>('');
  const [finalAssessmentReason, setFinalAssessmentReason] = useState('');
  const [suppressNotification, setSuppressNotification] = useState(false);
  const [suppressionReason, setSuppressionReason] = useState('');
  // Phase 6 (OFFBOARD-03): populated when the server reports a non-zero
  // balance - counsellors are blocked outright; admins must acknowledge and
  // record a financial resolution before resubmitting.
  const [balanceBlock, setBalanceBlock] = useState<{ outstandingBalance: number; needsAck: boolean } | null>(null);
  const [balanceAcknowledged, setBalanceAcknowledged] = useState(false);
  const [balanceResolution, setBalanceResolution] = useState<'' | 'write_off' | 'payment_plan' | 'chase'>('');
  const [writeOffReason, setWriteOffReason] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [showDeleteWarning, setShowDeleteWarning] = useState(false);

  // Fetch client details when component mounts
  useEffect(() => {
    const fetchClientDetails = async () => {
      try {
        setLoading(true);
        console.log('Fetching client details for ID:', clientId);
        const response = await api.get(`/api/clients/${clientId}`);
        setClient(response.data);
        setError(null);
      } catch (err) {
        console.error('Error fetching client details:', err);
        setError('Failed to fetch client details. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    if (clientId) {
      fetchClientDetails();
    }
  }, [clientId]);

  const validateForm = (): boolean => {
    const validationErrors: Record<string, string> = {};

    if (!offboardingReason) {
      validationErrors.reason = 'Please select a reason for offboarding';
    }

    if (offboardingReason === 'Other' && !otherReason.trim()) {
      validationErrors.other = 'Please specify the other reason';
    }

    // OFFBOARD-04: the final assessment outcome must be recorded; a reason
    // is required for every outcome other than 'completed'.
    if (!finalAssessmentOutcome) {
      validationErrors.finalAssessmentOutcome = 'Please record the final assessment outcome';
    } else if (finalAssessmentOutcome !== 'completed' && !finalAssessmentReason.trim()) {
      validationErrors.finalAssessmentReason = 'Please record why a final assessment was not completed';
    }

    if (suppressNotification && !suppressionReason.trim()) {
      validationErrors.suppressionReason = 'A reason is required to suppress the client notification';
    }

    if (balanceBlock?.needsAck) {
      if (!balanceAcknowledged) {
        validationErrors.balanceAcknowledged = 'You must acknowledge the outstanding balance';
      }
      if (!balanceResolution) {
        validationErrors.balanceResolution = 'Please record how the outstanding balance will be resolved';
      } else if (balanceResolution === 'write_off' && !writeOffReason.trim()) {
        validationErrors.writeOffReason = 'Please record why the debt is being written off';
      }
    }

    setErrors(validationErrors);
    return Object.keys(validationErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    // If client has no sessions, show delete warning
    if (!client?.hasHadSessions && !showDeleteWarning) {
      setShowDeleteWarning(true);
      return;
    }

    try {
      setIsSubmitting(true);
      await api.post(`/api/clients/${clientId}/offboard`, {
        reason: offboardingReason === 'Other' ? otherReason : offboardingReason,
        finalAssessmentOutcome,
        finalAssessmentReason: finalAssessmentOutcome !== 'completed' ? finalAssessmentReason : undefined,
        suppressClientNotification: suppressNotification || undefined,
        notificationSuppressionReason: suppressNotification ? suppressionReason : undefined,
        balanceAcknowledged: balanceBlock?.needsAck ? balanceAcknowledged : undefined,
        balanceResolution: balanceBlock?.needsAck ? balanceResolution : undefined,
        writeOffReason: balanceResolution === 'write_off' ? writeOffReason.trim() : undefined
      });

      navigate('/');
    } catch (err: any) {
      console.error('Error offboarding client:', err);
      const data = err?.response?.data;
      // OFFBOARD-03: the server blocks counsellor offboarding on a non-zero
      // balance and requires an explicit admin acknowledgement + resolution.
      if (data?.code === 'OUTSTANDING_BALANCE') {
        setBalanceBlock({ outstandingBalance: data.outstandingBalance || 0, needsAck: false });
        setSubmitError(data.message);
        return;
      }
      if (data?.code === 'BALANCE_ACKNOWLEDGEMENT_REQUIRED') {
        setBalanceBlock({ outstandingBalance: data.outstandingBalance || 0, needsAck: true });
        setSubmitError(data.message);
        return;
      }
      setSubmitError(data?.message || 'Failed to offboard client. Please try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center glass-card">
        <div className="text-center glass-card p-8">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading client details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center glass-card">
        <div className="text-center glass-card p-8">
          <div className="text-red-600 mb-4">{error}</div>
          <Button
            variant="contained"
            onClick={() => navigate('/')}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              minWidth: '160px',
              background: 'linear-gradient(135deg, #4f46e5 0%, #6366f1 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #4338ca 0%, #4f46e5 100%)'
              }
            }}
          >
            Return Home
          </Button>
        </div>
      </div>
    );
  }

  return (
    <ThemeBackground variant="full">
      <div className="container mx-auto px-6 py-8 overflow-y-scroll">
        <div className="glass-card p-6 min-h-[calc(100vh-4rem)]">
          <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
            {/* Header Section */}
            <div className="glass-card-light mb-6">
            <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
              <div className="flex justify-between items-start">
                {/* Buttons on the left */}
                <div className="flex flex-col space-y-4">
                  {/* Primary Actions */}
                  <div className="flex space-x-4">
                    <Button
                      variant="contained"
                      sx={{
                        textTransform: 'none',
                        fontWeight: 700,
                        minWidth: '160px',
                        background: 'linear-gradient(135deg, #4f46e5 0%, #6366f1 100%)',
                        '&:hover': {
                          background: 'linear-gradient(135deg, #4338ca 0%, #4f46e5 100%)'
                        }
                      }}
                      onClick={() => navigate(`/client/${clientId}`)}
                    >
                      Back to Client Details
                    </Button>
                  </div>
                </div>

                {/* Title on the right */}
                <div className="text-right">
                  <Typography 
                    variant="h3" 
                    component="h1" 
                    gutterBottom
                    sx={{
                      fontFamily: 'Roboto',
                      fontWeight: 800,
                      fontSize: '2.5rem',
                      color: '#ffffff',
                      letterSpacing: '0.5px'
                    }}
                  >
                    Offboard Client
                  </Typography>
                  {client && (
                    <Typography 
                      variant="subtitle1" 
                      sx={{
                        color: 'rgba(255,255,255,0.75)',
                        fontWeight: 500,
                        fontSize: '1.1rem',
                        mb: 2
                      }}
                    >
                      {client.firstName} {client.lastName}
                    </Typography>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Main Content */}
          {client && (
            <div className="glass-card-light rounded-lg p-6 mb-6">
              <div className={`p-4 rounded-lg mb-6 ${
                client.hasHadSessions ? 'bg-yellow-50 border border-yellow-200' : 'bg-red-50 border border-red-200'
              }`}>
                <p className={`text-sm ${client.hasHadSessions ? 'text-yellow-800' : 'text-red-800'}`}>
                  {client.hasHadSessions ? (
                    <>
                      <span className="font-semibold">Note:</span> This client has had {client.numberOfSessions} {client.numberOfSessions === 1 ? 'session' : 'sessions'}. 
                      Their information will be retained for 7 years as per data retention policy. 
                      They can return and resume services after a new assessment.
                    </>
                  ) : (
                    <>
                      <span className="font-semibold">Warning:</span> This client has not had any sessions. 
                      Their record will be permanently deleted from the system.
                    </>
                  )}
                </p>
              </div>
              
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Reason for Offboarding:
                  </label>
                  <select
                    value={offboardingReason}
                    onChange={(e) => setOffboardingReason(e.target.value)}
                    className={`w-full p-2 border rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 ${
                      errors.reason ? 'border-red-500' : 'border-gray-300'
                    }`}
                  >
                    <option value="">Select a reason</option>
                    <option value="Completed">Completed Counselling</option>
                    <option value="Discontinued">Discontinued by Client</option>
                    <option value="Referred">Referred to Other Service</option>
                    <option value="NoSessions">No Sessions Attended</option>
                    <option value="Other">Other</option>
                  </select>
                  {errors.reason && (
                    <p className="mt-1 text-sm text-red-600">{errors.reason}</p>
                  )}
                </div>

                {offboardingReason === 'Other' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Please specify:
                    </label>
                    <textarea
                      value={otherReason}
                      onChange={(e) => setOtherReason(e.target.value)}
                      className={`w-full p-2 border rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 ${
                        errors.other ? 'border-red-500' : 'border-gray-300'
                      }`}
                      rows={3}
                    />
                    {errors.other && (
                      <p className="mt-1 text-sm text-red-600">{errors.other}</p>
                    )}
                  </div>
                )}

                {/* OFFBOARD-04: final assessment outcome */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Final Assessment Outcome:
                  </label>
                  <select
                    value={finalAssessmentOutcome}
                    onChange={(e) => setFinalAssessmentOutcome(e.target.value as FinalAssessmentOutcome)}
                    className={`w-full p-2 border rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 ${
                      errors.finalAssessmentOutcome ? 'border-red-500' : 'border-gray-300'
                    }`}
                  >
                    <option value="">Select an outcome</option>
                    <option value="completed">Final assessment completed</option>
                    <option value="declined">Client declined a final assessment</option>
                    <option value="not_possible">Final assessment not possible</option>
                    <option value="clinically_inappropriate">Final assessment clinically inappropriate</option>
                    <option value="other">Other</option>
                  </select>
                  {errors.finalAssessmentOutcome && (
                    <p className="mt-1 text-sm text-red-600">{errors.finalAssessmentOutcome}</p>
                  )}
                </div>

                {finalAssessmentOutcome && finalAssessmentOutcome !== 'completed' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Reason no final assessment was completed:
                    </label>
                    <textarea
                      value={finalAssessmentReason}
                      onChange={(e) => setFinalAssessmentReason(e.target.value)}
                      className={`w-full p-2 border rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 ${
                        errors.finalAssessmentReason ? 'border-red-500' : 'border-gray-300'
                      }`}
                      rows={2}
                    />
                    {errors.finalAssessmentReason && (
                      <p className="mt-1 text-sm text-red-600">{errors.finalAssessmentReason}</p>
                    )}
                  </div>
                )}

                {/* OFFBOARD-03: outstanding balance block / admin resolution */}
                {balanceBlock && (
                  <div className="p-4 rounded-lg bg-red-50 border border-red-200">
                    <p className="text-sm text-red-800 font-semibold mb-1">
                      Outstanding balance: £{(balanceBlock.outstandingBalance / 100).toFixed(2)}
                    </p>
                    {submitError && <p className="text-sm text-red-700 mb-3">{submitError}</p>}
                    {balanceBlock.needsAck && (
                      <div className="space-y-3 mt-2">
                        <label className="flex items-start space-x-2 text-sm text-gray-800">
                          <input
                            type="checkbox"
                            checked={balanceAcknowledged}
                            onChange={(e) => setBalanceAcknowledged(e.target.checked)}
                            className="mt-1"
                          />
                          <span>I acknowledge this client has an outstanding balance which will be preserved and not erased by offboarding.</span>
                        </label>
                        {errors.balanceAcknowledged && (
                          <p className="text-sm text-red-600">{errors.balanceAcknowledged}</p>
                        )}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Financial resolution:
                          </label>
                          <select
                            value={balanceResolution}
                            onChange={(e) => setBalanceResolution(e.target.value as typeof balanceResolution)}
                            className={`w-full p-2 border rounded-md ${
                              errors.balanceResolution ? 'border-red-500' : 'border-gray-300'
                            }`}
                          >
                            <option value="">Select a resolution</option>
                            <option value="payment_plan">Payment plan agreed</option>
                            <option value="chase">To be chased / collected</option>
                            <option value="write_off">Written off</option>
                          </select>
                          {errors.balanceResolution && (
                            <p className="mt-1 text-sm text-red-600">{errors.balanceResolution}</p>
                          )}
                        </div>
                        {balanceResolution === 'write_off' && (
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Write-off reason:
                            </label>
                            <input
                              type="text"
                              value={writeOffReason}
                              onChange={(e) => setWriteOffReason(e.target.value)}
                              placeholder="e.g. Financial hardship, goodwill, disputed charge"
                              className={`w-full p-2 border rounded-md ${
                                errors.writeOffReason ? 'border-red-500' : 'border-gray-300'
                              }`}
                            />
                            {errors.writeOffReason && (
                              <p className="mt-1 text-sm text-red-600">{errors.writeOffReason}</p>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* OFFBOARD-06: admin-only notification suppression */}
                {isAdmin && (
                  <div className="p-4 rounded-lg glass-card border border-gray-200">
                    <label className="flex items-start space-x-2 text-sm text-gray-800">
                      <input
                        type="checkbox"
                        checked={suppressNotification}
                        onChange={(e) => setSuppressNotification(e.target.checked)}
                        className="mt-1"
                      />
                      <span>Suppress the client-facing offboarding notification (exceptional circumstances only).</span>
                    </label>
                    {suppressNotification && (
                      <div className="mt-3">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Reason for suppressing the notification:
                        </label>
                        <textarea
                          value={suppressionReason}
                          onChange={(e) => setSuppressionReason(e.target.value)}
                          className={`w-full p-2 border rounded-md ${
                            errors.suppressionReason ? 'border-red-500' : 'border-gray-300'
                          }`}
                          rows={2}
                        />
                        {errors.suppressionReason && (
                          <p className="mt-1 text-sm text-red-600">{errors.suppressionReason}</p>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {submitError && !balanceBlock && (
                  <div className="p-4 rounded-lg bg-red-50 border border-red-200">
                    <p className="text-sm text-red-800">{submitError}</p>
                  </div>
                )}

                {/* Delete Warning Modal */}
                {showDeleteWarning && (
                  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                    <div className="bg-white rounded-lg p-6 max-w-md w-full">
                      <div className="mb-4">
                        <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
                          <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                          </svg>
                        </div>
                        <Typography 
                          variant="h6" 
                          component="h3"
                          align="center"
                          sx={{
                            fontFamily: 'Roboto',
                            fontWeight: 500,
                            color: 'rgba(255,255,255,0.85)',
                            mb: 2
                          }}
                        >
                          Confirm Permanent Deletion
                        </Typography>
                      </div>
                      <p className="text-sm text-gray-500 mb-6">
                        This client has not had any sessions. Their record will be permanently deleted. 
                        This action cannot be undone. Are you sure you want to proceed?
                      </p>
                      <div className="flex justify-end space-x-4">
                        <Button
                          onClick={() => setShowDeleteWarning(false)}
                          sx={{
                            textTransform: 'none',
                            fontWeight: 500
                          }}
                        >
                          Cancel
                        </Button>
                        <Button
                          type="submit"
                          variant="contained"
                          color="error"
                          sx={{
                            textTransform: 'none',
                            fontWeight: 700
                          }}
                        >
                          Delete Record
                        </Button>
                      </div>
                    </div>
                  </div>
                )}

                <div className="flex justify-end space-x-4 pt-4">
                  <Button
                    onClick={() => navigate(`/client/${clientId}`)}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 500
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    color="error"
                    disabled={isSubmitting}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 700,
                      minWidth: '160px',
                    }}
                  >
                    {isSubmitting ? (
                      <>
                        <span className="animate-spin inline-block h-4 w-4 border-b-2 border-white rounded-full mr-2"></span>
                        Processing...
                      </>
                    ) : (
                      'Offboard Client'
                    )}
                  </Button>
                </div>
              </form>
            </div>
          )}
        </div>
      </div>
      </div>
    </ThemeBackground>
  );
};

export default OffboardingClient;